// Smycles "Fachkompetenz" skill scale - 7 ordered levels grouped into 3
// categories. Genesis stores the level as a percent (~14, 29, ..., 100);
// to/from-percent helpers handle the wire boundary. `name` is only the
// fallback label; UI code should resolve `nameId`, `titleId`, and
// `descriptionId` through i18n.

export interface SkillLevel {
  value: number;
  name: string;
  nameId: string;
  titleId: string;
  categoryId: string;
  descriptionId: string;
}

export interface SkillCategory {
  nameId: string;
  levels: SkillLevel[];
}

export const SKILL_LEVELS: readonly SkillLevel[] = [
  { value: 1, name: "Explorer", nameId: "skill.levelName.1", titleId: "skill.levelTitle.1", categoryId: "skill.catAwareness", descriptionId: "skill.desc.1" },
  { value: 2, name: "Builder", nameId: "skill.levelName.2", titleId: "skill.levelTitle.2", categoryId: "skill.catAwareness", descriptionId: "skill.desc.2" },
  { value: 3, name: "Contributor", nameId: "skill.levelName.3", titleId: "skill.levelTitle.3", categoryId: "skill.catPractical", descriptionId: "skill.desc.3" },
  { value: 4, name: "Practitioner", nameId: "skill.levelName.4", titleId: "skill.levelTitle.4", categoryId: "skill.catPractical", descriptionId: "skill.desc.4" },
  { value: 5, name: "Specialist", nameId: "skill.levelName.5", titleId: "skill.levelTitle.5", categoryId: "skill.catPractical", descriptionId: "skill.desc.5" },
  { value: 6, name: "Multiplier", nameId: "skill.levelName.6", titleId: "skill.levelTitle.6", categoryId: "skill.catImpact", descriptionId: "skill.desc.6" },
  { value: 7, name: "Pioneer", nameId: "skill.levelName.7", titleId: "skill.levelTitle.7", categoryId: "skill.catImpact", descriptionId: "skill.desc.7" },
] as const;

export const LEVEL_COUNT = 7;

export const SKILL_CATEGORIES: readonly SkillCategory[] = SKILL_LEVELS.reduce<SkillCategory[]>((acc, l) => {
  let group = acc.find((g) => g.nameId === l.categoryId);
  if (!group) {
    group = { nameId: l.categoryId, levels: [] };
    acc.push(group);
  }
  group.levels.push(l);
  return acc;
}, []);

export function toStoragePercent(level: number): number {
  if (!level || level <= 0) return 0;
  const c = Math.min(Math.max(level, 1), LEVEL_COUNT);
  return Math.round(c * (100 / LEVEL_COUNT));
}

export function fromStoragePercent(percent: number | null | undefined): number {
  const p = Number(percent);
  if (!p || p <= 0) return 0;
  // Legacy 9-step grid values. Flyway remaps these in Genesis, but this keeps
  // the UI semantically correct when pointed at an unmigrated local backend.
  switch (Math.round(p)) {
    case 11: return 1;
    case 22: return 2;
    case 33: return 3;
    case 44: return 4;
    case 56:
    case 67: return 5;
    case 78:
    case 89: return 6;
    default: break;
  }
  if (p >= 100) return LEVEL_COUNT;
  return Math.min(Math.max(Math.round(p / (100 / LEVEL_COUNT)), 1), LEVEL_COUNT);
}

export function levelFromPercent(percent: number | null | undefined): SkillLevel | null {
  return SKILL_LEVELS.find((l) => l.value === fromStoragePercent(percent)) ?? null;
}
